import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogType?: string;
  ogImage?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  schema?: Record<string, unknown>;
}

function setMetaTag(name: string, content: string, propName?: string, propValue?: string) {
  const attr = propName || 'name';
  const val = propValue || name;
  let el = document.querySelector(`meta[${attr}="${val}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, val);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMetaTag(name: string) {
  const el = document.querySelector(`meta[name="${name}"]`);
  if (el) el.remove();
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  articlePublishedTime,
  articleModifiedTime,
  schema,
}: SEOHeadProps) {
  const location = useLocation();
  const baseUrl = `https://climaxpro.github.io`;
  const fullUrl = `${baseUrl}${canonicalUrl}`;

  useEffect(() => {
    document.title = title;

    setMetaTag('description', description);
    setMetaTag('keywords', keywords);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', fullUrl);

    setMetaTag('og:title', title, 'property', 'og:title');
    setMetaTag('og:description', description, 'property', 'og:description');
    setMetaTag('og:type', ogType, 'property', 'og:type');
    setMetaTag('og:url', fullUrl, 'property', 'og:url');
    setMetaTag('og:site_name', '亲密指南', 'property', 'og:site_name');
    setMetaTag('og:locale', 'zh_CN', 'property', 'og:locale');
    if (ogImage) setMetaTag('og:image', ogImage, 'property', 'og:image');

    setMetaTag('twitter:card', 'summary_large_image', 'name', 'twitter:card');
    setMetaTag('twitter:title', title, 'name', 'twitter:title');
    setMetaTag('twitter:description', description, 'name', 'twitter:description');
    if (ogImage) setMetaTag('twitter:image', ogImage, 'name', 'twitter:image');

    if (articlePublishedTime) setMetaTag('article:published_time', articlePublishedTime, 'property', 'article:published_time');
    if (articleModifiedTime) setMetaTag('article:modified_time', articleModifiedTime, 'property', 'article:modified_time');

    if (schema) {
      let scriptEl = document.querySelector('script[data-schema="page"]');
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.setAttribute('type', 'application/ld+json');
        scriptEl.setAttribute('data-schema', 'page');
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(schema);
    }

    return () => {
      if (schema) {
        const scriptEl = document.querySelector('script[data-schema="page"]');
        if (scriptEl) scriptEl.remove();
      }
    };
  }, [title, description, keywords, fullUrl, ogType, ogImage, articlePublishedTime, articleModifiedTime, schema]);

  return null;
}